import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { RealtimeEvent, type NotificationDto } from '@fas/types';
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@fas/ui';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useMe } from '@/lib/permissions';
import { setAppBadge } from '@/lib/pwa';
import { useRealtimeEvent } from '@/lib/realtime';

/** In-app notifications (§36): unread count in the header, latest items in a menu. */
export function NotificationsBell() {
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const timeZone = me?.institution?.timezone;
  const unread = useQuery({
    queryKey: ['/notifications', 'unread-count'],
    queryFn: ({ signal }) => api.get<{ unread: number }>('/notifications/unread-count', undefined, signal),
    refetchInterval: 120_000,
  });
  const latest = useQuery({
    queryKey: ['/notifications', 'latest'],
    queryFn: ({ signal }) => api.getPage<NotificationDto>('/notifications', { pageSize: 8 }, signal).then((r) => r.data),
  });
  useRealtimeEvent(RealtimeEvent.NOTIFICATION_CREATED, () => void queryClient.invalidateQueries({ queryKey: ['/notifications'] }));
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['/notifications'] });
  const markRead = useMutation({ mutationFn: (id: string) => api.post(`/notifications/${id}/read`), onSuccess: refresh });
  const markAll = useMutation({ mutationFn: () => api.post('/notifications/read-all'), onSuccess: refresh });
  const count = unread.data?.unread ?? 0;
  // The installed app's icon shows the unread count too (where the platform supports badges).
  useEffect(() => {
    if (unread.data) setAppBadge(count);
  }, [count, unread.data]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label={count > 0 ? `Notifications, ${count} unread` : 'Notifications'}>
          <Bell aria-hidden />
          {count > 0 ? (
            <span className="absolute -right-0.5 -top-0.5 grid min-w-5 place-items-center rounded-full bg-red-700 px-1 text-[11px] font-semibold text-white" aria-hidden>
              {count > 99 ? '99+' : count}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80">
        <DropdownMenuLabel>Notifications</DropdownMenuLabel>
        {latest.data && latest.data.length === 0 ? <p className="px-2 py-3 text-sm text-slate-600">No notifications yet.</p> : null}
        {latest.data?.map((n) => (
          <DropdownMenuItem
            key={n.id}
            className="flex-col items-start gap-0.5"
            onSelect={() => {
              if (!n.readAt) markRead.mutate(n.id);
              if (n.link) navigate(n.link);
            }}
          >
            <span className="flex w-full items-center justify-between gap-2">
              <span className={n.readAt ? 'font-normal' : 'font-semibold'}>{n.title}</span>
              {!n.readAt ? <span className="text-[11px] font-semibold uppercase text-blue-800">New</span> : null}
            </span>
            <span className="line-clamp-2 text-xs text-slate-600">{n.body}</span>
            <span className="text-[11px] text-slate-500">{formatDateTime(n.createdAt, timeZone)}</span>
          </DropdownMenuItem>
        ))}
        {count > 0 ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => markAll.mutate()}>Mark all as read</DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
